import { defineStyle, defineStyleConfig } from '@chakra-ui/react';
import { transparentize } from '@chakra-ui/theme-tools';

const variantPrimary = defineStyle((props) => ({
  bg: `${props.colorScheme}.500`,
  color: 'white',
  _active: { bg: `${props.colorScheme}.700`, boxShadow: 'none' },
  _hover: {
    bg: `${props.colorScheme}.500`,
    boxShadow: '0 0 0 4px #F0E9F8',
    color: 'white',
    _disabled: {
      bg: `${props.colorScheme}.200`,
      color: 'white',
      boxShadow: 'none',
    },
  },
  _disabled: {
    bg: `${props.colorScheme}.200`,
    color: 'white',
  },

  _focusVisible: {
    ringColor: `${props.colorScheme}.700`,
  },
}));

const variantSecondary = defineStyle((props) => ({
  bg: 'white',
  color: `${props.colorScheme}.500`,
  textColor: `${props.colorScheme}.500`,
  border: '1px solid',
  borderColor: 'gray.100',
  _hover: {
    bg: 'white',
    color: `${props.colorScheme}.500`,
    textColor: `${props.colorScheme}.500`,
    border: '1px solid',
    borderColor: 'gray.100',
    boxShadow: '0 0 0 4px #F0E9F8',
    _disabled: {
      bg: 'white',
      textColor: `${props.colorScheme}.200`,
      color: `${props.colorScheme}.200`,
      borderColor: 'gray.100',
    },
  },
  _active: {
    bg: `${props.colorScheme}.25`,
    color: `${props.colorScheme}.700`,
    textColor: `${props.colorScheme}.700`,
    borderColor: 'gray.100',
    boxShadow: 'none',
  },
  _focusVisible: {
    ringColor: `${props.colorScheme}.300`,
  },
  _disabled: {
    bg: 'white',
    color: `${props.colorScheme}.200`,
    textColor: `${props.colorScheme}.200`,
    borderColor: 'gray.100',
  },
}));

const sm = defineStyle({
  fontSize: 'sm',
  px: '4',
  py: '2',
  h: '9',
  borderRadius: 'lg',
});

const md = defineStyle({
  fontSize: 'md',
  px: '5',
  py: '3',
  h: '12',
  borderRadius: 'xl',
});

const lg = defineStyle({
  fontSize: 'lg',
  fontWeight: 'bold',
  px: '5',
  py: '3',
  h: '12',
  borderRadius: 'xl',
});

export const buttonTheme = defineStyleConfig({
  baseStyle: (props) => ({
    fontWeight: 'medium',
    boxShadow: 'sm',
    border: '1px solid transparent',
    _focusVisible: {
      boxShadow: 'none',
      ring: '2px',
      ringOffset: '2px',
      ringColor: `${props.colorScheme}.500`,
    },
    // Disabled Style
    ...(props.isDisabled && props.variant !== 'link'
      ? {
          _disabled: {
            opacity: 0.8,
            border: '1px solid!',
            bg: 'blackAlpha.50!',
            borderColor: 'blackAlpha.50!',
            color: 'blackAlpha.300!',
            _dark: {
              opacity: 1,
              bg: 'whiteAlpha.50!',
              borderColor: 'whiteAlpha.50!',
              color: 'whiteAlpha.300!',
            },
          },
        }
      : { _disabled: {} }),
  }),
  variants: {
    // Custom variants
    '@primary': (props) => variantPrimary({ ...props, colorScheme: 'brand' }),
    '@secondary': (props) =>
      variantSecondary({ ...props, colorScheme: 'brand' }),
    '@dangerPrimary': (props) =>
      variantPrimary({ ...props, colorScheme: 'error' }),
    '@dangerSecondary': (props) =>
      variantSecondary({ ...props, colorScheme: 'error' }),
    // Default variants
    link: {
      boxShadow: 'none',
    },
    solid: (props) =>
      props.colorScheme === 'gray' ? variantSecondary(props) : {},
    outline: variantSecondary,
    ghost: (props) => ({
      bg: transparentize(`${props.colorScheme}.500`, 0.05)(props.theme),
      _hover: {
        bg: transparentize(`${props.colorScheme}.700`, 0.15)(props.theme),
      },
    }),
  },
  sizes: {
    lg,
    md,
    sm,
  },
});
